import Phaser from 'phaser';
import { colors } from 'Phaser/config';
import { ReactToPhaserListeners, NotifyReactEvents, RemoveListener } from 'Phaser/EventBus2';
import Instruction, { InstructionPosition } from 'Phaser/GameObjects/Instruction';
import { Timestamp } from 'Types/ExerciseData';

export interface IObjective {
  text?: string;
  instructionWaitTime?: number;
  videoSkipDestination?: Timestamp;
  videoCompleteDestination?: Timestamp;
}

export interface IConfig<Objective extends IObjective> {
  chapterIdx?: number;
  objectives: Objective[];
  firstObjectiveAppearDelay?: number;
}

export default abstract class ExerciseBase<
  Objective extends IObjective,
  Config extends IConfig<Objective>,
>
  extends Phaser.Scene
{
  active: boolean = false;
  // The instruction to be shown to the user
  instruction: Instruction | null = null;
  numFailedAttempts: number = 0;
  failMessage: Phaser.GameObjects.Text | null = null;
  toastMessage: Phaser.GameObjects.Text | null = null;
  debugText: Phaser.GameObjects.Text | null = null;
  paused: boolean = false;
  config?: Config;
  currentObjective?: number;
  waitToast: boolean = false;

  registeredKeys: Map<string, Phaser.Input.Keyboard.Key> = new Map();
  skipped: boolean = false;
  videoResumeTimeout?: Phaser.Time.TimerEvent;

  /**
   * Returns a valid objective index, clamped to valid bounds.
   * Use this when you need a guaranteed-valid index for accessing objectives.
   * Returns 0 if no objectives exist or currentObjective is undefined.
   */
  protected getValidObjectiveIndex(): number {
    if (!this.config?.objectives?.length) return 0;
    if (this.currentObjective === undefined || this.currentObjective < 0) {
      return 0;
    }
    return Math.min(this.currentObjective, this.config.objectives.length - 1);
  }

  /**
   * Returns the current objective with bounds checking.
   * Returns undefined if no config or objectives exist.
   * Use this instead of direct `this.config.objectives[this.currentObjective]` access
   * for safety against out-of-bounds crashes.
   */
  protected getCurrentObjective(): Objective | undefined {
    if (!this.config?.objectives?.length) return undefined;
    return this.config.objectives[this.getValidObjectiveIndex()];
  }

  /**
   * Checks if we're currently at the last objective.
   * Safe to call even if currentObjective is undefined or out of bounds.
   */
  protected isLastObjective(): boolean {
    if (!this.config?.objectives?.length) return true;
    return this.getValidObjectiveIndex() === this.config.objectives.length - 1;
  }

  showInstruction(instruction: string, position: InstructionPosition = 'Top'): void {
    this.instruction = new Instruction(this, instruction, position);
  }

  debugLog(text: string): void {
    if (process.env.REACT_APP_NODE_ENV === 'prd' || process.env.REACT_APP_NODE_ENV === 'stg')
      return;
    if (!this.debugText)
      this.debugText = this.add.text(200, 100, text, {
        color: '#000',
        fontSize: 22,
      });
    else this.debugText.setText(text);
  }

  setPlaybackRate({ rate: _rate }: { rate: number }) {
    //this.sound.setRate(rate);
  }

  // Initialise objects and perform any scene switch transitions here.
  init(config: Config): void {
    this.events.once('shutdown', this.unload, this);
    ReactToPhaserListeners.StartExercise(() => this.startExercise(), this);
    ReactToPhaserListeners.PauseExercise(() => this.pauseExercise(), this);
    ReactToPhaserListeners.MidiOn((data) => this.midiOn(data), this);
    ReactToPhaserListeners.MidiOff((data) => this.midiOff(data), this);
    ReactToPhaserListeners.SkipExercise(() => this.skipExercise(), this);
    ReactToPhaserListeners.UnloadExercise(() => this.stop(), this);
    ReactToPhaserListeners.ChangePlaybackRate((data) => this.setPlaybackRate(data), this);
    ReactToPhaserListeners.ToggleExercise(() => this.toggle(), this);
    this.config = config;
    this.active = true;
    // Reset per-session state that may be stale from a previous scene run.
    // With the singleton game, the scene instance persists across mounts.
    this.paused = false;
    this.currentObjective = undefined;
    this.numFailedAttempts = 0;
    this.skipped = false;
    this.waitToast = false;
    this.instruction = null;
    this.failMessage = null;
    this.toastMessage = null;
    this.debugText = null;
  }

  onExerciseShow() {}

  transition(skip?: boolean, last?: boolean): void {
    // Guard against calling transition before scene is fully initialized
    // currentObjective can be -1 (not started), undefined, or a valid index
    if (!this.config || this.currentObjective === undefined || this.currentObjective < 0) {
      return;
    }

    // Additional guard: don't transition if paused or inactive
    if (this.paused || !this.active) {
      return;
    }

    if (last) {
      if (!this.skipped) {
        NotifyReactEvents.ExercisePassed();
      }
      this.onExercisePass();
    }
    if (skip) {
      this.onObjectiveSkipped();
      const objective = this.config.objectives[this.currentObjective];
      if (objective?.videoSkipDestination) {
        NotifyReactEvents.ObjectiveSkipped({ jumpTo: objective.videoSkipDestination });
      }
    } else {
      this.onObjectivePassed();
      const objective = this.config.objectives[this.currentObjective];
      if (objective?.videoSkipDestination) {
        if (last) {
          // Fire ObjectiveComplete synchronously for the last objective.
          // onExerciseOver schedules stop() at 500ms, and stop() calls
          // unload() which kills all timers. If ObjectiveComplete is also
          // on a 500ms timer, the two race — stop() can fire first and
          // kill ObjectiveComplete before it seeks the video forward.
          NotifyReactEvents.ObjectiveComplete({ jumpTo: objective.videoSkipDestination! });
        } else {
          this.videoResumeTimeout = this.time.delayedCall(500, () =>
            NotifyReactEvents.ObjectiveComplete({ jumpTo: objective.videoSkipDestination! }),
          );
        }
      }
    }
    if (last) {
      this.onExerciseOver();
    }
  }

  midiOn(_data?: { note: number; velocity?: number }): void {}

  midiOff(_data?: { note: number }): void {}

  /// Remove all events / timers / event listeners here,
  /// all sub classes should call super.clearEvents() if overridden
  clearEvents(): void {
    RemoveListener('MidiOn');
    RemoveListener('MidiOff');
    RemoveListener('PauseExercise');
    RemoveListener('UpdateObjective');
    RemoveListener('SkipExercise');
    RemoveListener('UnloadExercise');
    RemoveListener('ToggleExercise');
  }

  /// Should be called before loading the next scene.
  unload(): void {
    //NotifyReact("exercise-paused");
    this.currentObjective = undefined;
    NotifyReactEvents.HideExercise();
    RemoveListener('StartExercise');
    RemoveListener('UpdateObjective');
    this.debugText?.destroy();
    this.debugText = null;
    this.active = false;
    this.failMessage?.destroy();
    this.failMessage = null;
    this.toastMessage?.destroy();
    this.toastMessage = null;
    this.instruction?.destroy();
    this.instruction = null;
    this.registeredKeys.clear();
    this.numFailedAttempts = 0;
    // Clean up videoResumeTimeout before clearing all events
    if (this.videoResumeTimeout) {
      this.videoResumeTimeout.destroy();
      this.videoResumeTimeout = undefined;
    }
    this.clearEvents();
    this.time.removeAllEvents();
    this.tweens.killAll();
    if (this.paused) this.scene.stop('PauseMenu');
    //this.scene.start("Empty");
  }

  changeScene(
    name: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Phaser scene start args
    args: any,
  ): void {
    this.scene.start(name, args);
  }

  startExercise(): void {}

  pauseExercise(): void {
    this.paused = true;
    this.scene.pause();
    this.scene.launch('PauseMenu', { other: this.scene.key });
  }

  hideInstruction(): void {
    this.instruction?.destroy();
  }

  onObjectivePassed(): void {}
  onObjectiveSkipped(): void {}

  passObjective(last?: boolean): void {
    // Guard against calling passObjective before scene is fully initialized
    // currentObjective can be -1 (not started), undefined, or a valid index
    if (!this.config || this.currentObjective === undefined || this.currentObjective < 0) {
      return;
    }

    // CRITICAL: Don't pass if scene is paused or not active
    // This prevents auto-complete when app resumes from background
    if (this.paused || !this.active) {
      return;
    }

    const clipIdx = this.registry.get('clipIdx') as number;
    this.registry.set(
      'clipIdx',
      clipIdx + 1 >= this.registry.get('feedbackClipCount') ? 0 : clipIdx + 1,
    );
    this.instruction?.showResult(true, () => {
      this.sound.play(this.registry.get('feedbackClipRandomDistribution')[clipIdx]);
      this.transition(false, last);
    });
  }

  skipExercise(): void {
    // Guard against calling skip before scene is fully initialized
    if (!this.config || this.currentObjective === undefined) {
      console.warn('skipExercise called before scene fully initialized, ignoring');
      return;
    }

    this.skipped = true;
    this.instruction?.destroy();
    this.transition(true, this.isLastObjective());
  }

  /// Override this for custom behaviour when a user passes the exercise
  onExercisePass(): void {}

  restart(): void {}

  /// Override this for custom behaviour when a user fails the exercise
  onExerciseFail(): void {
    this.restart();
  }

  onExerciseOver(delay: number = 500): void {
    this.time.delayedCall(delay, this.stop, [], this);
  }

  abstract stop(): void;
  abstract create(): void;

  showToast(text: string): void {
    if (this.waitToast) return;
    this.waitToast = true;
    const dpr = 1;
    this.toastMessage?.destroy();
    this.toastMessage = this.add.text(100, 100, text, {
      fontFamily: 'Lato',
      fontSize: `${32 * dpr}px`,
      color: '#000000',
      align: 'center',
    });
    this.toastMessage.setAlpha(0);
    this.toastMessage.setPosition(
      this.cameras.main.width / 2 - this.toastMessage.width / 2,
      this.cameras.main.height - 75 * dpr,
    );
    this.tweens.add({
      hold: 1000,
      targets: this.toastMessage,
      alpha: 1,
      duration: 500,
      yoyo: true,
      ease: 'Power1',
      onComplete: () => {
        //bg.destroy();
        this.toastMessage?.destroy();
        this.waitToast = false;
      },
    });
  }

  resume(): void {
    console.log('[ExerciseBase] resume called, setting paused=false');
    this.paused = false;
  }

  toggle(): void {
    if (!this.paused) this.pauseExercise();
  }

  failExercise(text?: string): void {
    this.numFailedAttempts++;
    if (this.numFailedAttempts >= 1) {
      const cam = this.cameras.main;
      const dpr = 1;
      this.failMessage = this.add.text(
        cam.width / 2,
        cam.height + 100 * dpr,
        text ?? 'Good effort! Try again.',
        {
          fontFamily: 'Lato',
          fontSize: 32 * dpr,
          fontStyle: 'bold',
          color: colors.textFg,
        },
      );
      this.tweens.add({
        targets: this.failMessage,
        y: cam.height - 120 * dpr,
        duration: 500,
        ease: 'Power2',
        yoyo: true,
        hold: 2000,
      });
      this.failMessage.setX(cam.width / 2 - this.failMessage.displayWidth / 2);
      NotifyReactEvents.SkipButtonRequest();
    }
    this.onExerciseFail();
    /*this.failMenu?.destroy();
    this.failMenu = new FailMenu(
      this,
      () => {
        this.failMenu?.hide();
        this.passExercise();
      },
      () => {
        this.failMenu?.hide();
        this.restart();
      }
    );*/
  }

  /// Add key press / release listeners for the duration of this exercise
  addKey(keyname: string, onDown: () => void, onUp?: () => void, ctx?: object) {
    const key = this.registeredKeys.get(keyname) ?? this.input.keyboard!.addKey(keyname);
    key.on('down', onDown, ctx);
    //this.input.keyboard!.on(`keydown-${key.toUpperCase()}`, onDown, ctx);
    if (onUp) {
      //this.input.keyboard!.on(`keyup-${key.toUpperCase()}`, onUp, ctx);
      key.on('up', onUp, ctx);
    }
    this.registeredKeys.set(keyname, key);
  }

  /// Remove any registered key listeners with the passed callbacks
  removeKey(keyname: string, onDown: () => void, onUp?: () => void) {
    this.registeredKeys.get(keyname)?.off('down', onDown).off('up', onUp);
  }

  centreHeight(objectHeight: number) {
    return this.cameras.main.height / 2 - objectHeight / 2;
  }

  centreWidth(objectWidth: number) {
    return this.cameras.main.width / 2 - objectWidth / 2;
  }

  sectionComplete(): void {}
}
